import { useCallback, useRef, useState } from "react";

import moment from "moment";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    JiraFFLContentGenerationResponse,
    MadlibContentGenerationChildWorkItem,
    WizardCreateProjectSampleIssue,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";
import { useCloudStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";
import { fallbackCgsData } from "@/templates/paid-landing/utils/paid-jira-ai-ffl.ts";

import { confiugredIssueTypes } from "../views/setup/jira/constants";
import { Transition, Priority } from "../views/setup/jira/utils";
import {
    TemplateOptionKanban,
    TemplateOptionScrum
} from "../views/setup/types.tsx";

const PROJECT_CREATE_TASK_POLLING_TIMEOUT = 25000;
const DEFAULT_FFL_PROJECT_TEMPLATE = "KANBAN";
const DEFAULT_FFL_PROJECT_KEY = "PROJ";
const DEFAULT_STATUS_NAMES = ["To Do", "In Progress", "Done"];
const DEFAULT_END_DATE_DAYS_FROM_NOW = 30;
const JIRA_FFL_EXPERIMENT_KEY = "cfind6215FrictionlessFirstLookJiraV2";

const setProjectCreateTaskIdQueryParam = (taskId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("taskId", taskId);
    window.history.replaceState({}, "", url.toString());
};

const getElapsedDaysFromNow = (timestamp: number): number => {
    const now = moment();
    const targetDate = moment(timestamp);
    return targetDate.diff(now, "days");
};

const generateSequentialDueDates = (
    endDate: number,
    numberOfIssues: number
): string[] => {
    const startDate = moment().startOf("day");
    const finalDate = moment(endDate).startOf("day");
    const totalDays = finalDate.diff(startDate, "days");
    const intervalSizeInDays = totalDays / numberOfIssues;

    return Array.from({ length: numberOfIssues }, (_, index) => {
        const intervalStartDay = Math.floor(index * intervalSizeInDays);
        const intervalEndDay = Math.floor((index + 1) * intervalSizeInDays);
        const randomDay =
            intervalStartDay +
            Math.floor(
                Math.random() * Math.max(1, intervalEndDay - intervalStartDay)
            );

        return startDate.clone().add(randomDay, "days").format("YYYY-MM-DD");
    });
};

const mapFflResponseToSampleIssues = (
    response: JiraFFLContentGenerationResponse,
    dueDate: number
): WizardCreateProjectSampleIssue[] => {
    const { childWorkItems } = response.sampleIssues;

    const relativeDays = getElapsedDaysFromNow(dueDate);

    const issueDueDates = generateSequentialDueDates(
        dueDate,
        childWorkItems.length
    );
    const issueDueDateRelativeDays = issueDueDates.map((issueDueDate) =>
        getElapsedDaysFromNow(new Date(issueDueDate).getTime())
    );

    const childrenIssues: WizardCreateProjectSampleIssue[] = childWorkItems.map(
        (issue: MadlibContentGenerationChildWorkItem, index: number) => ({
            refId: `task-${index + 1}`,
            parentRefId: undefined,
            issueType: "Task",
            summary: issue.summary,
            description: "",
            transitionId: Transition.ToDo,
            relativeDays: issueDueDateRelativeDays[index] ?? relativeDays,
            priorityId: Priority.Medium,
            assigneeId: undefined
        })
    );

    return childrenIssues;
};

export type UseCreateJiraFflProjectParams = {
    readonly enabled: boolean;
    readonly cgsKey?: string;
    readonly onTaskIdCreated?: (taskId: string) => void;
};

/**
 * Optional payload for `createJiraProject`.
 * Pass `cgsData` to skip re-fetching FFL content by `cgsKey`.
 * Pass `template` so the created Jira project matches the setup preview (Scrum vs Kanban).
 */
export type CreateJiraFflProjectInput = {
    readonly cgsData?: JiraFFLContentGenerationResponse;
    /** Overrides `sampleIssues.spaceName` from CGS for the created project name. */
    readonly projectName?: string;
    readonly template?:
        | typeof TemplateOptionScrum
        | typeof TemplateOptionKanban;
};

export const resolveFflProjectTemplate = (
    input: CreateJiraFflProjectInput | undefined
): string => {
    const template = input?.template;
    if (template === TemplateOptionScrum) {
        return TemplateOptionScrum;
    }
    if (template === TemplateOptionKanban) {
        return TemplateOptionKanban;
    }
    return DEFAULT_FFL_PROJECT_TEMPLATE;
};

type CreateAnalyticsEventFn = ReturnType<
    typeof useAnalyticsEvents
>["createAnalyticsEvent"];

type StargateClient = ReturnType<typeof useBxpWebStargateClient>;

const shouldSkipJiraFflCreation = (
    enabled: boolean,
    cloudId: string,
    hasCreatedProject: boolean,
    requestInProgress: boolean,
    input: CreateJiraFflProjectInput | undefined,
    cgsKey: string | undefined
): boolean => {
    if (!enabled || !cloudId || hasCreatedProject || requestInProgress) {
        return true;
    }
    const hasPreloadedCgs = Boolean(input?.cgsData);
    const hasCgsKey = Boolean(cgsKey);
    return !hasPreloadedCgs && !hasCgsKey;
};

export const loadFflContentForProjectCreation = async (
    input: CreateJiraFflProjectInput | undefined,
    cgsKey: string | undefined,
    stargateClient: StargateClient
): Promise<JiraFFLContentGenerationResponse> => {
    if (input?.cgsData) {
        return input.cgsData;
    }
    if (cgsKey === "default") {
        return fallbackCgsData;
    }
    if (cgsKey) {
        return stargateClient.getJiraFFLContentByCgsKey(cgsKey);
    }
    throw new Error(
        "FFL project creation requires cgsKey or preloaded cgsData"
    );
};

const resolveFflProjectName = (
    input: CreateJiraFflProjectInput | undefined,
    response: JiraFFLContentGenerationResponse
): string => {
    const trimmedName = input?.projectName?.trim();
    if (trimmedName && trimmedName.length > 0) {
        return trimmedName;
    }
    return response.sampleIssues.spaceName ?? "Project";
};

const fireJiraFflCreationStarted = (
    createAnalyticsEvent: CreateAnalyticsEventFn,
    cgsKey: string | undefined,
    projectTemplate: string
) => {
    createAnalyticsEvent({
        eventType: "track",
        action: "started",
        actionSubject: "jiraFflProjectCreation",
        actionSubjectId: "jiraAnimatedProvisioningScreen",
        source: "/ondemand/signup/confirmation",
        attributes: {
            experiment: JIRA_FFL_EXPERIMENT_KEY,
            cgsKey,
            template: projectTemplate
        }
    }).fire();
};

const fireJiraFflCreationSucceeded = (
    createAnalyticsEvent: CreateAnalyticsEventFn,
    cgsKey: string | undefined,
    projectTemplate: string
) => {
    createAnalyticsEvent({
        eventType: "track",
        action: "succeeded",
        actionSubject: "jiraFflProjectCreation",
        actionSubjectId: "jiraAnimatedProvisioningScreen",
        source: "/ondemand/signup/confirmation",
        attributes: {
            experiment: JIRA_FFL_EXPERIMENT_KEY,
            cgsKey,
            template: projectTemplate
        }
    }).fire();
};

const fireJiraFflCreationFailed = (
    createAnalyticsEvent: CreateAnalyticsEventFn,
    cgsKey: string | undefined,
    error: unknown,
    projectTemplate: string
) => {
    createAnalyticsEvent({
        eventType: "track",
        action: "failed",
        actionSubject: "jiraFflProjectCreation",
        actionSubjectId: "jiraAnimatedProvisioningScreen",
        source: "/ondemand/signup/confirmation",
        attributes: {
            experiment: JIRA_FFL_EXPERIMENT_KEY,
            cgsKey,
            template: projectTemplate,
            error: error instanceof Error ? error.message : "Unknown error"
        }
    }).fire();
};

const runJiraFflProjectCreation = async ({
    input,
    cgsKey,
    cloudId,
    projectTemplate,
    stargateClient,
    createAnalyticsEvent,
    onTaskIdCreated
}: {
    input: CreateJiraFflProjectInput | undefined;
    cgsKey: string | undefined;
    cloudId: string;
    projectTemplate: string;
    stargateClient: StargateClient;
    createAnalyticsEvent: CreateAnalyticsEventFn;
    onTaskIdCreated?: (taskId: string) => void;
}) => {
    fireJiraFflCreationStarted(createAnalyticsEvent, cgsKey, projectTemplate);

    const response = await loadFflContentForProjectCreation(
        input,
        cgsKey,
        stargateClient
    );

    const dueDate =
        Date.now() + DEFAULT_END_DATE_DAYS_FROM_NOW * 24 * 60 * 60 * 1000;

    const sampleIssues = mapFflResponseToSampleIssues(response, dueDate);
    const projectName = resolveFflProjectName(input, response);

    const { taskId } = await stargateClient.postProjectCreate({
        projectName,
        cloudId,
        template: projectTemplate,
        issueTypes: confiugredIssueTypes,
        statusNames: DEFAULT_STATUS_NAMES,
        customDetail: {
            enableExtraFields: true,
            sampleDataVersion: "MANUAL"
        },
        projectKey: DEFAULT_FFL_PROJECT_KEY,
        sampleIssues
    });

    setProjectCreateTaskIdQueryParam(taskId);

    if (onTaskIdCreated) {
        onTaskIdCreated(taskId);
    }

    const { status } = await stargateClient.pollTaskUntilComplete(
        taskId,
        cloudId,
        PROJECT_CREATE_TASK_POLLING_TIMEOUT
    );

    if (status !== "COMPLETE") {
        throw new Error("FFL project creation did not complete");
    }

    fireJiraFflCreationSucceeded(createAnalyticsEvent, cgsKey, projectTemplate);
};

/**
 * For Jira FFL provisioning flows: once site provisioning completes, fetch the cached FFL
 * content (via cgsKey), create the onboarding project, and poll until completion.
 *
 * This hook exposes an imperative `createJiraProject` function (matching the TWC pattern)
 * and a `hasCreatedProject` boolean that callers can use to gate redirect/progress flows.
 */
export const useCreateJiraFflProject = ({
    enabled,
    cgsKey,
    onTaskIdCreated
}: UseCreateJiraFflProjectParams) => {
    const stargateClient = useBxpWebStargateClient();
    const { cloudId } = useCloudStatus();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const isCreateRequestInProgress = useRef(false);
    const [hasCreatedProject, setHasCreatedProject] = useState(false);

    const createJiraProject = useCallback(
        async (input?: CreateJiraFflProjectInput) => {
            if (
                shouldSkipJiraFflCreation(
                    enabled,
                    cloudId,
                    hasCreatedProject,
                    isCreateRequestInProgress.current,
                    input,
                    cgsKey
                )
            ) {
                return;
            }

            const projectTemplate = resolveFflProjectTemplate(input);

            try {
                isCreateRequestInProgress.current = true;

                await runJiraFflProjectCreation({
                    input,
                    cgsKey,
                    cloudId,
                    projectTemplate,
                    stargateClient,
                    createAnalyticsEvent,
                    onTaskIdCreated
                });

                setHasCreatedProject(true);
            } catch (error) {
                fireJiraFflCreationFailed(
                    createAnalyticsEvent,
                    cgsKey,
                    error,
                    projectTemplate
                );

                // Still mark as created to prevent retries and unblock flows that are gated on completion.
                setHasCreatedProject(true);
            } finally {
                isCreateRequestInProgress.current = false;
            }
        },
        [
            enabled,
            cloudId,
            cgsKey,
            hasCreatedProject,
            stargateClient,
            createAnalyticsEvent,
            onTaskIdCreated
        ]
    );

    return { hasCreatedProject, createJiraProject };
};
